// Shared primitives for structured chat cards (plan todo 23). All cards are
// mobile-first: 44px+ action targets, design-token colors only, active press
// feedback, no hover-dependent interaction.

import type { LucideIcon } from "lucide-react";
import { ArrowRight } from "lucide-react";
import { Card } from "../ui/Card";
import { cn } from "../../lib/cn";

export const CARD_BUTTON_BASE = cn(
  "flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-semibold",
  "select-none transition-all duration-200 ease-out active:scale-[0.98]",
  "disabled:cursor-not-allowed disabled:opacity-50",
);

export function ChatCardShell({
  icon: Icon,
  title,
  subtitle,
  testId,
  tone = "accent",
  children,
}: {
  readonly icon: LucideIcon;
  readonly title: string;
  readonly subtitle?: string;
  readonly testId: string;
  readonly tone?: "accent" | "warning";
  readonly children: React.ReactNode;
}) {
  return (
    <Card
      className={cn(
        "w-full max-w-[95%] border-l-4 p-4 shadow-none",
        tone === "warning" ? "border-l-warning" : "border-l-accent",
      )}
      data-testid={testId}
    >
      <div className="flex items-center gap-2.5">
        <div
          className={cn(
            "rounded-lg p-1.5",
            tone === "warning" ? "bg-warning/10 text-warning" : "bg-accent-light text-accent",
          )}
        >
          <Icon size={16} strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-text">{title}</div>
          {subtitle !== undefined && (
            <div className="text-xs text-text-muted">{subtitle}</div>
          )}
        </div>
      </div>
      <div className="mt-3 flex flex-col gap-3">{children}</div>
    </Card>
  );
}

export function CardActionButton({
  label,
  testId,
  variant = "secondary",
  disabled = false,
  onPress,
}: {
  readonly label: string;
  readonly testId: string;
  readonly variant?: "primary" | "secondary" | "ghost";
  readonly disabled?: boolean;
  readonly onPress: () => void;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      disabled={disabled}
      onClick={onPress}
      className={cn(
        CARD_BUTTON_BASE,
        variant === "primary" && "bg-accent text-white",
        variant === "secondary" && "border border-border bg-surface text-text",
        variant === "ghost" && "text-accent",
      )}
    >
      {label}
    </button>
  );
}

export function OpenInButton({
  label,
  screen,
  testId,
  onSwitchScreen,
}: {
  readonly label: string;
  readonly screen: string;
  readonly testId: string;
  readonly onSwitchScreen?: (screen: string) => void;
}) {
  if (onSwitchScreen === undefined) return null;
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => onSwitchScreen(screen)}
      className={cn(CARD_BUTTON_BASE, "text-accent")}
    >
      {label}
      <ArrowRight size={16} strokeWidth={2} />
    </button>
  );
}

export function MacroChip({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-text-muted">
      {label} {value}
    </span>
  );
}
